import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import StickyActions from '@/components/StickyActions.jsx';
import { Field } from '@/components/Field.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import { CREAR_ASIENTO, ACTUALIZAR_ASIENTO, GET_ASIENTO } from './gql.js';
import { LIST_CUENTAS } from '../Cuentas/gql.js';
import { LIST_CENTROS } from '../Centros/gql.js';

const origenOptions = [
  { value: 'manual', label: 'Manual' },
  { value: 'ventas', label: 'Ventas' },
  { value: 'compras', label: 'Compras' },
  { value: 'tesoreria', label: 'Tesorería' },
  { value: 'stock', label: 'Stock' },
];

export default function AsientoForm({ asiento, onSuccess, onCancel }) {
  const isEdit = Boolean(asiento?.id);
  const toast = useToast();
  const [form, setForm] = useState(() => createDefaultFormState());
  const [detalle, setDetalle] = useState(() => createDefaultDetalleState());
  const [lastLoadedId, setLastLoadedId] = useState(null);
  const [, crearAsiento] = useMutation(CREAR_ASIENTO);
  const [, actualizarAsiento] = useMutation(ACTUALIZAR_ASIENTO);
  const [cuentasResult] = useQuery({ query: LIST_CUENTAS, variables: { limit: 500, offset: 0 } });
  const [centrosResult] = useQuery({ query: LIST_CENTROS, variables: { empresaId: form.empresaId || '1' }, pause: !form.empresaId });
  const asientoId = asiento?.id;
  const [{ data: asientoData, fetching: asientoLoading, error: asientoError }] = useQuery({
    query: GET_ASIENTO,
    variables: { id: asientoId },
    pause: !isEdit || !asientoId || Boolean(asiento?.detalle),
  });

  useEffect(() => {
    if (isEdit) {
      const origenDatos = asiento?.detalle ? asiento : asientoData?.getAsiento;
      if (origenDatos && lastLoadedId !== origenDatos.id) {
        setForm({
          empresaId: origenDatos.empresaId ?? '1',
          fecha: origenDatos.fecha ?? new Date().toISOString().slice(0, 10),
          periodo: origenDatos.periodo ?? new Date().toISOString().slice(0, 7),
          origen: origenDatos.origen ?? 'manual',
          glosa: origenDatos.glosa ?? '',
        });
        setDetalle(
          (origenDatos.detalle ?? []).map((linea) => ({
            id: linea.id ?? createInternalId(),
            cuentaId: linea.cuentaId,
            centroId: linea.centroId ?? '',
            descripcion: linea.descripcion ?? '',
            debe: Number(linea.debe ?? 0),
            haber: Number(linea.haber ?? 0),
            moneda: linea.moneda ?? 'ARS',
            tc: Number(linea.tc ?? 1),
          })),
        );
        setLastLoadedId(origenDatos.id);
      }
    } else if (lastLoadedId !== 'create') {
      setForm(createDefaultFormState());
      setDetalle(createDefaultDetalleState());
      setLastLoadedId('create');
    }
  }, [isEdit, asiento, asientoData, lastLoadedId]);

  const cuentas = cuentasResult.data?.listCuentas?.items ?? [];
  const centros = useMemo(() => flattenCentros(centrosResult.data?.listCentros ?? []), [centrosResult.data]);

  const totales = detalle.reduce(
    (acc, linea) => {
      const debe = Number(linea.debe ?? 0);
      const haber = Number(linea.haber ?? 0);
      return { debe: acc.debe + debe, haber: acc.haber + haber };
    },
    { debe: 0, haber: 0 },
  );
  const diferencia = Number((totales.debe - totales.haber).toFixed(2));

  const handleLineaChange = (id, key, value) => {
    setDetalle((prev) => prev.map((linea) => (linea.id === id ? { ...linea, [key]: value } : linea)));
  };

  const addLinea = () => {
    setDetalle((prev) => [
      ...prev,
      { id: createInternalId(), cuentaId: '', centroId: '', descripcion: '', debe: 0, haber: 0, moneda: 'ARS', tc: 1 },
    ]);
  };

  const removeLinea = (id) => {
    setDetalle((prev) => prev.filter((linea) => linea.id !== id));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.fecha || !form.empresaId) {
      toast.error('Completá la fecha y la empresa.');
      return;
    }
    if (Math.abs(diferencia) > 0.01) {
      toast.error('El asiento debe cumplir la doble partida.');
      return;
    }
    const detalleValido = detalle.filter((linea) => Number(linea.debe ?? 0) > 0 || Number(linea.haber ?? 0) > 0);
    if (!detalleValido.length) {
      toast.error('Agregá líneas con importes.');
      return;
    }
    if (detalleValido.some((linea) => !linea.cuentaId)) {
      toast.error('Todas las líneas deben tener una cuenta contable.');
      return;
    }
    const payload = {
      empresaId: String(form.empresaId),
      fecha: form.fecha,
      periodo: form.periodo,
      origen: form.origen,
      glosa: form.glosa,
      detalle: detalleValido.map((linea) => ({
        cuentaId: linea.cuentaId,
        centroId: linea.centroId || null,
        descripcion: linea.descripcion,
        debe: Number(linea.debe ?? 0),
        haber: Number(linea.haber ?? 0),
        moneda: linea.moneda,
        tc: Number(linea.tc ?? 1),
      })),
    };
    try {
      const result = isEdit
        ? await actualizarAsiento({ id: asiento.id, input: { fecha: payload.fecha, periodo: payload.periodo, glosa: payload.glosa, detalle: payload.detalle } })
        : await crearAsiento({ input: payload });
      if (result.error) {
        throw result.error;
      }
      toast.success(isEdit ? 'Asiento actualizado' : 'Asiento creado');
      onSuccess?.();
    } catch (error) {
      toast.error(error.message ?? 'No pudimos guardar el asiento');
    }
  };

  if (isEdit && !asiento?.detalle) {
    if (asientoLoading) {
      return (
        <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.65)] p-6 text-sm text-[rgb(var(--muted))]">
          Cargando datos del asiento...
        </div>
      );
    }
    if (asientoError) {
      return (
        <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.65)] p-6 text-sm text-red-500">
          {asientoError.message}
        </div>
      );
    }
    if (!asientoData?.getAsiento) {
      return (
        <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.65)] p-6 text-sm text-red-500">
          No encontramos el asiento solicitado.
        </div>
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.92)] p-6 shadow-[0_20px_36px_-24px_rgba(var(--shadow),0.35)]">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-4">
            <Field label="Empresa">
              <input
                value={form.empresaId}
                onChange={(event) => setForm((prev) => ({ ...prev, empresaId: event.target.value }))}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Fecha">
              <input
                type="date"
                value={form.fecha}
                onChange={(event) => setForm((prev) => ({ ...prev, fecha: event.target.value }))}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Período">
              <input
                value={form.periodo}
                onChange={(event) => setForm((prev) => ({ ...prev, periodo: event.target.value }))}
                placeholder="YYYY-MM"
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Origen">
              <select
                value={form.origen}
                onChange={(event) => setForm((prev) => ({ ...prev, origen: event.target.value }))}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {origenOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12">
            <Field label="Glosa">
              <textarea
                value={form.glosa}
                onChange={(event) => setForm((prev) => ({ ...prev, glosa: event.target.value }))}
                rows={2}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                placeholder="Detalle del asiento"
              />
            </Field>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.92)] p-6 shadow-[0_20px_36px_-24px_rgba(var(--shadow),0.35)]">
        <header className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-[rgb(var(--fg))]">Detalle</h3>
            <p className="text-sm text-[rgb(var(--muted))]">Registrá las líneas en debe y haber.</p>
          </div>
          <button
            type="button"
            onClick={addLinea}
            className="rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            Agregar línea
          </button>
        </header>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-[rgb(var(--border))] text-[rgb(var(--muted))]">
              <tr>
                <th className="px-3 py-2 text-left">Cuenta</th>
                <th className="px-3 py-2 text-left">Centro</th>
                <th className="px-3 py-2 text-left">Descripción</th>
                <th className="px-3 py-2 text-right">Debe</th>
                <th className="px-3 py-2 text-right">Haber</th>
                <th className="px-3 py-2 text-left">Moneda</th>
                <th className="px-3 py-2 text-left">TC</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--border))]">
              {detalle.map((linea) => (
                <tr key={linea.id} className="align-top">
                  <td className="px-3 py-2">
                    <select
                      value={linea.cuentaId}
                      onChange={(event) => handleLineaChange(linea.id, 'cuentaId', event.target.value)}
                      className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                    >
                      <option value="">Seleccioná cuenta</option>
                      {cuentas.map((cuenta) => (
                        <option key={cuenta.id} value={cuenta.id}>
                          {cuenta.codigo} · {cuenta.nombre}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={linea.centroId}
                      onChange={(event) => handleLineaChange(linea.id, 'centroId', event.target.value)}
                      className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                    >
                      <option value="">Sin centro</option>
                      {centros.map((centro) => (
                        <option key={centro.id} value={centro.id}>
                          {centro.codigo} · {centro.nombre}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      value={linea.descripcion}
                      onChange={(event) => handleLineaChange(linea.id, 'descripcion', event.target.value)}
                      className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                      placeholder="Detalle"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={linea.debe}
                      onChange={(event) => handleLineaChange(linea.id, 'debe', event.target.value)}
                      className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-2 py-1 text-right focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={linea.haber}
                      onChange={(event) => handleLineaChange(linea.id, 'haber', event.target.value)}
                      className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-2 py-1 text-right focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={linea.moneda}
                      onChange={(event) => handleLineaChange(linea.id, 'moneda', event.target.value)}
                      className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                    >
                      <option value="ARS">ARS</option>
                      <option value="USD">USD</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      step="0.0001"
                      value={linea.tc}
                      onChange={(event) => handleLineaChange(linea.id, 'tc', event.target.value)}
                      className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => removeLinea(linea.id)}
                      className="rounded-xl border border-[rgb(var(--border))] px-2 py-1 text-xs text-[rgb(var(--muted))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                    >
                      Quitar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <footer className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[rgba(var(--subpanel),0.5)] px-4 py-3 text-sm">
          <div className="flex items-center gap-4">
            <span className="text-[rgb(var(--muted))]">Debe: <strong className="text-[rgb(var(--fg))]">{totales.debe.toFixed(2)}</strong></span>
            <span className="text-[rgb(var(--muted))]">Haber: <strong className="text-[rgb(var(--fg))]">{totales.haber.toFixed(2)}</strong></span>
          </div>
          <div className={`font-semibold ${Math.abs(diferencia) < 0.01 ? 'text-[rgb(var(--brand-700))]' : 'text-red-500'}`}>
            Diferencia: {diferencia.toFixed(2)}
          </div>
        </footer>
      </section>

      <StickyActions
        left={
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            Cancelar
          </button>
        }
        right={
          <button
            type="submit"
            className="rounded-xl bg-[rgb(var(--brand))] px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))]"
          >
            {isEdit ? 'Actualizar asiento' : 'Registrar asiento'}
          </button>
        }
      />
    </form>
  );
}

function flattenCentros(nodes, acc = []) {
  for (const node of nodes) {
    acc.push({ id: node.id, codigo: node.codigo, nombre: node.nombre });
    if (node.hijos?.length) {
      flattenCentros(node.hijos, acc);
    }
  }
  return acc;
}

function createDefaultFormState() {
  const now = new Date();
  return {
    empresaId: '1',
    fecha: now.toISOString().slice(0, 10),
    periodo: now.toISOString().slice(0, 7),
    origen: 'manual',
    glosa: '',
  };
}

function createDefaultDetalleState() {
  return [
    { id: createInternalId(), cuentaId: '', centroId: '', descripcion: '', debe: 0, haber: 0, moneda: 'ARS', tc: 1 },
    { id: createInternalId(), cuentaId: '', centroId: '', descripcion: '', debe: 0, haber: 0, moneda: 'ARS', tc: 1 },
  ];
}

function createInternalId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
